import { Title } from '@sr-sdks/api-sdk-axios';
import { Skeleton } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../../../../api/task/taskApi';
import { queryOptionsGetTitle } from '../../../../api/titles/titlesApi';
import {
  AppPagination,
  AppPaginationFooter,
} from '../../../../components/AppPagination';
import { EmbeddedWorkflowView } from '../../../../components/EmbeddedWorkflowView';
import { EntityList } from '../../../../components/EntityList';
import { HeaderField } from '../../../../components/HeaderField';
import { PageHeader } from '../../../../components/PageHeader';
import { ErrorFallback } from '../../../../components/QueryResults/ErrorFallback';
import { SingleQueryResults } from '../../../../components/QueryResults/SingleQueryResults';
import { RoyaltiesRegistrationAction } from '../../../../components/RoyaltiesRegistrationAction';
import {
  TaskCard,
  buildTaskCardViewUrl,
} from '../../../../components/TaskCard';
import { formatTitleType } from '../../../../components/TitleCard/titleMeta';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { useClientNamesByIds } from '../../../../hooks/useClientNames';
import { useEmbeddedWorkflowActive } from '../../../../hooks/useEmbeddedWorkflowActive';
import { usePaginationBounds } from '../../../../hooks/usePaginationBounds';
import { useTaskPartyNames } from '../../../../hooks/useTaskPartyNames';
import { MSR3BaseLayout } from '../../../../layout/MSR3BaseLayout';
import { WorkspaceTask } from '../../../../types/taskTypes';
import { buildEmbedWorkflowUrl } from '../../../../utils/embedWorkflow';
import {
  isRoyaltyServiceType,
  workspaceTypeIsAgent,
} from '../../../../utils/helpers/common';
import { CollectionAccountManagementSectionV3 } from './CollectionAccountManagementSectionV3';
import { TitleStatementsSection } from './TitleStatementsSection';

// One wide page of this title's tasks, filtered server-side by `titleIds`.
// A single title won't realistically carry more open tasks than this.
const TASKS_PAGE_SIZE = 200;
// Tasks are already fully in memory (see TASKS_PAGE_SIZE above), so this page
// paginates the filtered list client-side rather than re-querying per page.
const TASKS_DISPLAY_PAGE_SIZE = 4;

interface TitleSummaryHeaderProps {
  title: Title;
  titleId: string;
  workspaceId?: string;
}

/**
 * Sticky page header: title name, Year of production / Type / Client (Client
 * shown only for agency workspaces and omitted when the title has no
 * associated client), and the single
 * "View Registration" action. Back navigation to the titles list is already
 * covered by the persistent sidebar nav, so no in-page back affordance is
 * rendered here.
 *
 * `title` is guaranteed-resolved data passed down from the page-level
 * `SingleQueryResults` gate in `TitleSummaryV3` — this component only ever
 * mounts once that query has succeeded, so it needs no loading/error
 * branching of its own.
 */
const TitleSummaryHeader = ({
  title,
  titleId,
  workspaceId,
}: TitleSummaryHeaderProps) => {
  const { search } = useLocation();
  const [activeWorkspace] = useActiveWorkspace();
  const isAgent = workspaceTypeIsAgent(activeWorkspace);
  // By id: the workspace-wide fetch caps at 100 and loses the rest (APP-2388).
  const { getClientNamesOrFallback, isLoading: isClientNamesLoading } =
    useClientNamesByIds(title.ownerClientIds, {
      enabled: isAgent,
      workspaceId,
    });

  const clientNames = getClientNamesOrFallback(title.ownerClientIds);

  // Held with a placeholder while in flight, as `TitleCard` does — otherwise
  // "no client" and "not loaded yet" look identical.
  const isClientNamePending =
    isClientNamesLoading && Boolean(title.ownerClientIds?.length);

  // Reuses the same MSR2 "title" embed workflow that draft-registration task
  // cards already deep-link into (see `buildTaskEmbedPath`) — it's the only
  // existing embed route scoped to a single title.
  const registrationUrl = buildEmbedWorkflowUrl(
    `embed/title/${titleId}`,
    search,
    title.title,
  );
  // Per APP-2072's mutually-exclusive title-family decision, "Royalties
  // Registration" only applies to Royalties ANZ/World titles — a
  // CAM/DAS/Residuals-only title has no registration to view here.
  const isRoyaltyTitle = title.serviceType.some(isRoyaltyServiceType);

  return (
    <PageHeader
      title={title.title}
      actions={
        registrationUrl &&
        isRoyaltyTitle && (
          <RoyaltiesRegistrationAction
            label="Royalties Registration"
            // Primary, as it was before the gate; the modal trigger defaults to outline.
            buttonProps={{ variant: 'default' }}
            to={registrationUrl}
          />
        )
      }
    >
      <div className="flex flex-wrap gap-6">
        <HeaderField label="Year of production" value={title.year ?? ''} />
        <HeaderField
          label="Type"
          value={formatTitleType(title.titleType) ?? ''}
        />
        {isAgent && (isClientNamePending || clientNames.length > 0) && (
          <HeaderField
            label="Client"
            value={
              isClientNamePending ? (
                <Skeleton className="h-4 w-32" />
              ) : (
                clientNames.join(', ')
              )
            }
          />
        )}
      </div>
    </PageHeader>
  );
};

interface TitleSummaryContentProps {
  titleId: string;
}

const TitleSummaryContent = ({ titleId }: TitleSummaryContentProps) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;

  const tasksQuery = useQuery(
    queryOptionsGetTasksPaginated({
      pageSize: TASKS_PAGE_SIZE,
      titleIds: [titleId],
      workspaceId,
    }),
  );

  const tasks = (tasksQuery.data?.items as WorkspaceTask[]) ?? [];

  const [tasksPage, setTasksPage] = useState(1);

  // A different title's task list can be shorter than the page the user was
  // last on — reset rather than land on a stale, out-of-range page.
  useEffect(() => {
    setTasksPage(1);
  }, [titleId]);

  const totalTasksPages = Math.ceil(tasks.length / TASKS_DISPLAY_PAGE_SIZE);
  const { clampedPage: clampedTasksPage } = usePaginationBounds({
    page: tasksPage,
    setPage: setTasksPage,
    totalPages: totalTasksPages,
  });
  const paginatedTasks = tasks.slice(
    (clampedTasksPage - 1) * TASKS_DISPLAY_PAGE_SIZE,
    clampedTasksPage * TASKS_DISPLAY_PAGE_SIZE,
  );
  const { getPartyNames, isWorkspaceTypePending } = useTaskPartyNames(
    paginatedTasks,
    workspaceId,
  );

  // "View Registration" opens the same embedded-workflow view used across the
  // rest of MSR3 (Add Title, task "View" buttons) rather than a bespoke modal.
  const isEmbeddingWorkflow = useEmbeddedWorkflowActive();

  if (isEmbeddingWorkflow) return <EmbeddedWorkflowView />;

  return (
    <>
      <SingleQueryResults
        loadingFallback={null}
        notFetched={null}
        query={tasksQuery}
      >
        {() =>
          // Held back like the loading fallback until cards know their client field.
          tasks.length > 0 &&
          !isWorkspaceTypePending && (
            <EntityList>
              <EntityList.Header>
                <EntityList.Title>Tasks</EntityList.Title>
                <EntityList.Count count={tasks.length} />

                {tasks.length > TASKS_DISPLAY_PAGE_SIZE && (
                  <EntityList.Actions>
                    <AppPagination
                      currentPage={clampedTasksPage}
                      totalPages={totalTasksPages}
                      onPageChange={setTasksPage}
                    />
                  </EntityList.Actions>
                )}
              </EntityList.Header>

              <EntityList.Content>
                {paginatedTasks.map((task) => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    type={task.type}
                    {...getPartyNames(task)}
                    viewUrl={buildTaskCardViewUrl(task, task.type, search)}
                    workspaceId={workspaceId}
                    onDeleted={() => {
                      navigate(`/workspace/${workspaceId}/titles`, {
                        replace: true,
                      });
                    }}
                  />
                ))}
              </EntityList.Content>

              <AppPaginationFooter
                currentPage={clampedTasksPage}
                pageSize={TASKS_DISPLAY_PAGE_SIZE}
                totalItems={tasks.length}
                totalPages={totalTasksPages}
                onPageChange={setTasksPage}
              />
            </EntityList>
          )
        }
      </SingleQueryResults>

      <TitleStatementsSection titleId={titleId} />

      <CollectionAccountManagementSectionV3 titleId={titleId} />
    </>
  );
};

/**
 * MSR3 Title Summary page (`/workspace/:workspaceId/titles/summary/:titleId`).
 * Assembles the sticky header (name, Year of production / Type / Client, View
 * Registration), the Tasks list, and the two pre-built statements /
 * collection-account-management sections into one page shell.
 *
 * The entire page hangs off a single root-level title fetch (`titleQuery`),
 * gated through one `SingleQueryResults`. This replaces what used to be
 * per-component loading/error handling (the header ran its own duplicate
 * title query with its own `isPending`/`isError` JSX branches, and the
 * content's child sections each ran their own too) — with three different
 * error UIs able to appear at once on a failed fetch. Now:
 * - Loading and error states each render `MSR3BaseLayout` directly (with a
 *   skeleton or the shared `ErrorFallback` as content), so the sidebar/mobile
 *   header chrome stays mounted and usable in every state, not just success.
 * - On success, `title` is passed down as a plain, guaranteed-non-null prop
 *   to `TitleSummaryHeader`, which no longer fetches or branches on its own.
 * - `TitleStatementsSection` and `CollectionAccountManagementSectionV3` still
 *   run their own independent title queries (intentionally left as-is, for
 *   reuse outside this page's gate) — but since they only mount once this
 *   root query has already succeeded, their own error paths are effectively
 *   unreachable from this page.
 */
const TitleSummaryV3 = () => {
  const { titleId, workspaceId } = useParams<{
    titleId: string;
    workspaceId: string;
  }>();

  const titleQuery = useQuery(queryOptionsGetTitle(titleId));

  if (!titleId) return null;

  return (
    <SingleQueryResults
      errorFallback={(error) => (
        <MSR3BaseLayout
          classes={{ childrenWrapper: 'space-y-8' }}
          pageHeader={<PageHeader title="Unable to load title" />}
        >
          <ErrorFallback error={error} />
        </MSR3BaseLayout>
      )}
      loadingFallback={
        <MSR3BaseLayout
          classes={{ childrenWrapper: 'space-y-8' }}
          pageHeader={<Skeleton className="h-10 w-72" />}
        >
          <Skeleton className="h-64 w-full" />
        </MSR3BaseLayout>
      }
      query={titleQuery}
    >
      {(title) => (
        <MSR3BaseLayout
          classes={{ childrenWrapper: 'space-y-8' }}
          pageHeader={
            <TitleSummaryHeader
              title={title}
              titleId={titleId}
              workspaceId={workspaceId}
            />
          }
        >
          <TitleSummaryContent titleId={titleId} />
        </MSR3BaseLayout>
      )}
    </SingleQueryResults>
  );
};

export { TitleSummaryV3 };
